import { useEffect, useState } from "react";
import { useSession } from "../../context/SessionContext";
import { useAccess } from "../../hooks/useAccess";
import { useApi } from "../../hooks/useApi";
import { t } from "../../i18n";
import { UPGRADE_EVENT, showError, showSuccess } from "../../lib/feedback";
import { packagesService } from "../../services/packages";

export type KitKind = "proc" | "opme";

interface CatalogItem {
  id: number;
  code?: string;
  name: string;
}

// remove "kit"/"pacote" (com ou sem "de") do começo e capitaliza a primeira letra; o backend faz o mesmo
export const normalizeKitName = (input: string) => {
  const rest = input.replace(/^\s*(kit|pacote)(\s+de)?\s+/i, "").trim();
  return rest.charAt(0).toUpperCase() + rest.slice(1);
};

// Construtor de pacote: catálogo de cada tipo, a quantidade escolhida por item e o salvar.
// Cada tipo guarda a sua seleção, então trocar de tipo não apaga nada.
export const useKitBuilder = (onCreated: (kind: KitKind, name: string) => void) => {
  const { metadata, setMetadata, setBilling } = useSession();
  const { canCreateKit } = useAccess();
  const { apiFetch } = useApi();
  const [catalogs, setCatalogs] = useState<Record<KitKind, CatalogItem[] | null>>({ proc: null, opme: null });
  const [selection, setSelection] = useState<Record<KitKind, Record<number, number>>>({ proc: {}, opme: {} });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fail = (kind: KitKind) => (err: unknown) => {
      setCatalogs((c) => ({ ...c, [kind]: [] }));
      showError(err);
    };
    packagesService
      .procedimentos(apiFetch, metadata.especialidade)
      .then((list) => setCatalogs((c) => ({ ...c, proc: list.map((p) => ({ id: p.id, code: p.cod_procedimento, name: p.nome })) })))
      .catch(fail("proc"));
    packagesService
      .opmes(apiFetch)
      .then((list) => setCatalogs((c) => ({ ...c, opme: list.map((o) => ({ id: o.id, name: o.nome })) })))
      .catch(fail("opme"));
  }, [apiFetch, metadata.especialidade]);

  // 0 tira o item do pacote.
  const setQuantity = (kind: KitKind, id: number, quantity: number) =>
    setSelection((s) => {
      const { [id]: _, ...rest } = s[kind];
      return { ...s, [kind]: quantity > 0 ? { ...rest, [id]: quantity } : rest };
    });

  const create = async (kind: KitKind, rawName: string) => {
    const name = normalizeKitName(rawName);
    const resource = kind === "opme" ? "pacotes_opme" : "pacotes_cirurgia";
    if (!canCreateKit(resource)) {
      window.dispatchEvent(new CustomEvent(UPGRADE_EVENT, { detail: t("kitBuilder.limit") }));
      return;
    }

    const items = Object.entries(selection[kind]).map(([id, quantidade]) => ({
      item: catalogs[kind]!.find((c) => c.id === Number(id))!,
      quantidade,
    }));
    const base = { nome: name, especialidade: metadata.especialidade, medico_email: metadata.email };
    const body =
      kind === "opme"
        ? {
            ...base,
            opmes: items.map(({ item, quantidade }) => ({ opme_id: item.id, nome: item.name, quantidade })),
            total_pacotes_opme: metadata.total_pacotes_opme + 1,
          }
        : {
            ...base,
            procedimentos: items.map(({ item, quantidade }) => ({
              procedimento_id: item.id,
              cod_procedimento: item.code,
              nome: item.name,
              quantidade,
            })),
            total_pacotes_cirurgia: metadata.total_pacotes_cirurgia + 1,
          };

    setSaving(true);
    try {
      await packagesService.salvarPersonalizado(apiFetch, kind === "opme" ? "opmes" : "procedimentos", body);
    } catch (err) {
      showError(err);
      setSaving(false);
      return;
    }

    const counter = kind === "opme" ? "total_pacotes_opme" : "total_pacotes_cirurgia";
    setMetadata((prev) => ({ ...prev, [counter]: prev[counter] + 1 }));
    setBilling((prev) => {
      if (!prev) return prev;
      const used = { ...(prev.uso ?? prev.uso_free ?? {}) };
      used[resource] = (used[resource] ?? 0) + 1;
      return { ...prev, uso: used, uso_free: used };
    });
    setSaving(false);
    showSuccess("kitCreated");
    onCreated(kind, name);
  };

  return { catalogs, selection, setQuantity, create, saving };
};
